import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Divider, Grid, IconButton } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { incidentsService, AuthenticationProvider } from '@/services/incidentsService.ts';
import FormTextField from '@/components/form/FormTextField.tsx';
import FieldWithIcon from '@/components/form/FieldWithIcon.tsx';
import { required, requiredArray } from '@core/utils/validation.ts';
import FormSelect from '@/components/form/FormSelect.tsx';
import { useNotifications } from '@core/hooks/useNotifications.ts';

interface AccessConfiguration {
    id?: string;
    name: string;
    authenticationProviders: ShopAutomationAuthenticationProvider[];
    isActiveOnStoreOperationModes: string[];
}

interface ShopAutomationAuthenticationProvider {
    authenticationProviderId: string;
}

interface AccessConfigurationsFieldsProps {
    value: AccessConfiguration[];
    activeOperationModes: string[];
    onChange: (configs: AccessConfiguration[]) => void;
}

const AccessConfigurationsFields: React.FC<AccessConfigurationsFieldsProps> = ({
    value,
    activeOperationModes,
    onChange
}) => {
    const { t } = useTranslation();
    const [configurations, setConfigurations] = useState<AccessConfiguration[]>(value);
    const [authProviders, setAuthProviders] = useState<AuthenticationProvider[]>([]);
    const { notifyErrorResponse } = useNotifications();

    const loadAuthProviders = useCallback(async () => {
        try {
            const providers = await incidentsService.getAuthenticationProviders();
            setAuthProviders(providers);
        } catch (error) {
            console.error('Failed to load authentication providers:', error);
            notifyErrorResponse(t('errors.unknownError'));
        }
    }, []);

    useEffect(() => {
        void loadAuthProviders();
    }, [loadAuthProviders]);

    const handleAdd = () => {
        const newConfig: AccessConfiguration = {
            name: '',
            authenticationProviders: [],
            isActiveOnStoreOperationModes: []
        };
        const updated = [...configurations, newConfig];
        setConfigurations(updated);
        onChange?.(updated);
    };

    const handleRemove = (index: number) => {
        const updated = [...configurations];
        updated.splice(index, 1);
        setConfigurations(updated);
        onChange?.(updated);
    };

    const handleChange = (index: number, field: keyof AccessConfiguration, value: any) => {
        const updated = [...configurations];
        updated[index] = { ...updated[index], [field]: value };
        setConfigurations(updated);
        onChange?.(updated);
    };

    return (
        <Box>
            {configurations.map((config, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <FormTextField
                            name={`accessConfigurationName_${index}`}
                            rules={[required(t)]}
                            required
                            fullWidth
                            label={t('name')}
                            value={config.name}
                            onChange={(e) => handleChange(index, 'name', e.target.value)}
                        />
                    </Grid>
                    <Grid item md={6} sx={{ display: { xs: 'none', md: 'block' } }} />

                    <Grid item xs={12} md={6}>
                        <FormSelect
                            name={`authenticationProvider_${index}`}
                            rules={[requiredArray(t)]}
                            required
                            items={authProviders}
                            itemText="name"
                            itemValue="id"
                            label={t('authenticationProvider')}
                            value={config.authenticationProviders.map((x) => x.authenticationProviderId)}
                            onChange={(e) =>
                                handleChange(
                                    index,
                                    'authenticationProviders',
                                    (e || []).map((id: string) => ({ authenticationProviderId: id }))
                                )
                            }
                            multiple
                            sx={{ width: '100%' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FieldWithIcon
                            select={
                                <FormSelect
                                    name={`activeOperationModes_${index}`}
                                    rules={[requiredArray(t)]}
                                    required
                                    items={activeOperationModes.map((mode) => ({ name: mode, value: mode }))}
                                    itemText="name"
                                    itemValue="value"
                                    label={t('activeOperationModes')}
                                    value={config.isActiveOnStoreOperationModes}
                                    onChange={(e) => handleChange(index, 'isActiveOnStoreOperationModes', e)}
                                    multiple
                                    sx={{ width: '100%' }}
                                />
                            }
                            icon={
                                <IconButton onClick={() => handleRemove(index)} sx={{ fontSize: 18 }}>
                                    <i className="bi bi-trash3" />
                                </IconButton>
                            }
                        />
                    </Grid>
                </Grid>
            ))}

            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 2 }}>
                <Button variant="text" startIcon={<AddIcon />} onClick={handleAdd} sx={{ textTransform: 'none' }}>
                    {t('addAccessConfiguration')}
                </Button>
            </Box>
        </Box>
    );
};

export default AccessConfigurationsFields;
